import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Card, CardHeader, CardTitle, CardContent, Button, Input, Select, Badge } from './ui';

/**
 * Team management for an organization admin.
 *
 * Org tenancy Phase 4. Before this, admitting a hire meant an admin running an UPDATE
 * against profiles by hand - Phase 2 deliberately made a bare signup produce an account
 * with no org, which closed the security hole and left onboarding manual. An invite
 * carries the org and the role, so this screen is what replaces the SQL.
 *
 * Rendered only for admins; every endpoint behind it is also admin-only server-side, so
 * hiding it is convenience and not the control.
 */

interface Member {
  id: string;
  email: string;
  full_name: string | null;
  role: string;
  is_active: boolean;
  created_at: string;
}

interface Invite {
  id: string;
  email: string;
  role: string;
  status: string;
  expires_at: string;
  created_at: string;
}

const ROLES = ['analyst', 'broker', 'admin', 'investor', 'owner', 'tenant'];
const ROLE_OPTIONS = ROLES.map((r) => ({ value: r, label: r }));

const STATUS_TONE: Record<string, string> = {
  pending: 'bg-warning-wash text-warning border-warning/30',
  accepted: 'bg-success-wash text-success border-success/30',
  revoked: 'bg-gray-100 text-gray-500 border-gray-200',
  expired: 'bg-gray-100 text-gray-500 border-gray-200',
};

export default function TeamSettings({ currentUserId }: { currentUserId?: string }) {
  const [members, setMembers] = useState<Member[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('analyst');
  const [creating, setCreating] = useState(false);

  // The token comes back exactly once and is never recoverable, so it is held here until
  // the admin has copied it. Losing it means revoke and re-invite.
  const [freshLink, setFreshLink] = useState('');
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const [m, i] = await Promise.all([api.getMembers(), api.getInvites()]);
      setMembers(m.members);
      setInvites(i.invites);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not load the team');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    setFreshLink('');
    setCopied(false);
    try {
      const res = await api.createInvite(inviteEmail.trim(), inviteRole);
      setFreshLink(`${window.location.origin}/signup?invite=${res.token}`);
      setInviteEmail('');
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not create the invite');
    } finally {
      setCreating(false);
    }
  };

  const handleRole = async (id: string, role: string) => {
    setError('');
    try {
      await api.updateMember(id, { role });
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not change the role');
    }
  };

  const handleActive = async (id: string, is_active: boolean) => {
    setError('');
    try {
      await api.updateMember(id, { is_active });
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not update the member');
    }
  };

  const handleRevoke = async (id: string) => {
    setError('');
    try {
      await api.revokeInvite(id);
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not revoke the invite');
    }
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(freshLink);
    setCopied(true);
  };

  const pending = invites.filter((i) => i.status === 'pending');
  const past = invites.filter((i) => i.status !== 'pending');

  return (
    <Card>
      <CardHeader>
        <CardTitle>Team</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* ---- Invite ---- */}
        <form onSubmit={handleInvite} className="space-y-3">
          <p className="text-sm font-medium text-gray-700">Invite someone</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              type="email"
              required
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="colleague@apex-res.com"
              className="flex-1"
            />
            <Select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
              options={ROLE_OPTIONS}
            />
            <Button type="submit" isLoading={creating} disabled={creating}>
              Send invite
            </Button>
          </div>
        </form>

        {freshLink && (
          <div className="bg-info-wash border border-info/30 rounded-lg p-4 space-y-2">
            <p className="text-sm font-medium text-info">
              Invite link created. Copy it now - it is shown only once.
            </p>
            <div className="flex gap-2">
              <input
                readOnly
                value={freshLink}
                onFocus={(e) => e.currentTarget.select()}
                className="flex-1 text-xs font-mono bg-white border border-info/30 rounded px-2 py-1"
              />
              <Button type="button" variant="secondary" onClick={copyLink}>
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
            <p className="text-xs text-info">
              Send it to them directly. Anyone holding this link can create the account, so
              treat it like a password.
            </p>
          </div>
        )}

        {/* ---- Members ---- */}
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">
            Members {loading ? '' : `(${members.length})`}
          </p>
          {loading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : (
            <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg">
              {members.map((m) => (
                <div key={m.id} className="flex items-center justify-between gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-sm text-gray-900 truncate">
                      {m.full_name || m.email}
                      {m.id === currentUserId && (
                        <span className="ml-2 text-xs text-gray-400">you</span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{m.email}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {!m.is_active && <Badge className="bg-gray-100 text-gray-500">inactive</Badge>}
                    {/* The server refuses these too - an admin removing their own admin
                        role would lock the org out of its own member management. */}
                    <Select
                      value={m.role}
                      onChange={(e) => handleRole(m.id, e.target.value)}
                      disabled={m.id === currentUserId}
                      options={ROLE_OPTIONS}
                      className="text-sm"
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => handleActive(m.id, !m.is_active)}
                      disabled={m.id === currentUserId}
                    >
                      {m.is_active ? 'Deactivate' : 'Reactivate'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ---- Invites ---- */}
        {pending.length > 0 && (
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Pending invites</p>
            <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg">
              {pending.map((i) => (
                <div key={i.id} className="flex items-center justify-between gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-sm text-gray-900 truncate">{i.email}</p>
                    <p className="text-xs text-gray-500">
                      {i.role} · expires {new Date(i.expires_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Button type="button" variant="secondary" onClick={() => handleRevoke(i.id)}>
                    Revoke
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {past.length > 0 && (
          <details>
            <summary className="text-sm text-gray-500 cursor-pointer">
              Past invites ({past.length})
            </summary>
            <div className="mt-2 space-y-1">
              {past.map((i) => (
                <div key={i.id} className="flex items-center justify-between text-xs px-1">
                  <span className="text-gray-600 truncate">{i.email}</span>
                  <span className={`px-2 py-0.5 rounded border ${STATUS_TONE[i.status] || ''}`}>
                    {i.status}
                  </span>
                </div>
              ))}
            </div>
          </details>
        )}
      </CardContent>
    </Card>
  );
}
